import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X, Phone, User, LogOut, LayoutDashboard, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Acasă" },
  { to: "/produse", label: "Produse" },
  { to: "/oferte", label: "Oferte" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="bg-emerald-600 text-white" data-testid="free-shipping-bar">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-6 py-1.5 text-xs md:justify-between lg:px-12">
          <span className="label-caps flex items-center gap-1.5 text-center">
            <Truck className="h-3.5 w-3.5 shrink-0" /> Transport GRATUIT la toate comenzile · Livrare în toată țara
          </span>
          <a href="tel:+40734154845" className="hidden items-center gap-1.5 font-semibold md:flex" data-testid="topbar-phone">
            <Phone className="h-3.5 w-3.5" /> +40 734 154 845
          </a>
        </div>
      </div>
      <nav className="border-b border-slate-200/60 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img src="/logo-icon.png" alt="Mimaso" className="h-10 w-auto" />
            <span className="font-display text-2xl font-black tracking-tight text-slate-900">mimaso</span>
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-sm font-semibold text-slate-600 transition-colors hover:text-sky-600"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {user ? (
              <div className="hidden items-center gap-2 md:flex">
                {user.role === "admin" && (
                  <Button asChild variant="ghost" size="sm" data-testid="nav-admin">
                    <Link to="/admin"><LayoutDashboard className="mr-1.5 h-4 w-4" /> Admin</Link>
                  </Button>
                )}
                <Button asChild variant="ghost" size="sm" data-testid="nav-account">
                  <Link to="/contul-meu"><User className="mr-1.5 h-4 w-4" /> {user.name.split(" ")[0]}</Link>
                </Button>
                <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="nav-logout">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex" data-testid="nav-login">
                <Link to="/autentificare"><User className="mr-1.5 h-4 w-4" /> Cont</Link>
              </Button>
            )}

            <Link to="/cos" className="relative" data-testid="nav-cart">
              <Button variant="outline" size="icon" className="rounded-full border-slate-200">
                <ShoppingCart className="h-5 w-5" />
              </Button>
              {count > 0 && (
                <span className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-[11px] font-bold text-white" data-testid="cart-count">
                  {count}
                </span>
              )}
            </Link>

            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {open && (
          <div className="border-t border-slate-100 bg-white px-6 py-4 md:hidden">
            <div className="flex flex-col gap-3">
              {links.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-1 font-semibold text-slate-700" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                  {l.label}
                </Link>
              ))}
              <div className="my-2 h-px bg-slate-100" />
              {user ? (
                <>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="py-1 font-semibold text-sky-600">Panou Admin</Link>}
                  <Link to="/contul-meu" onClick={() => setOpen(false)} className="py-1 font-semibold text-slate-700">Contul meu</Link>
                  <button onClick={() => { handleLogout(); setOpen(false); }} className="py-1 text-left font-semibold text-red-500">Deconectare</button>
                </>
              ) : (
                <Link to="/autentificare" onClick={() => setOpen(false)} className="py-1 font-semibold text-sky-600">Autentificare</Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
